import { useState, useMemo } from "react";
import { useAuth } from "../App";
import {
  Search,
  BookOpen,
  FileText,
  GitBranch,
  Users,
  Calendar,
  BarChart3,
  Settings,
  Shield,
  ChevronDown,
  ChevronRight,
  Mail,
  Clock,
  CheckCircle2,
  Lightbulb,
  HelpCircle,
  Play,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";

const HELP_ARTICLES = [
  {
    category: "Getting Started",
    icon: BookOpen,
    color: "text-emerald-600",
    articles: [
      {
        id: "gs-1",
        title: "Welcome to Transaction Manager",
        content: `Transaction Manager is a comprehensive tool for managing real estate contracts from execution to closing. Here's a quick overview of what you can do:\n\n**Dashboard** — See your upcoming tasks, overdue items, and transaction summary at a glance.\n\n**Transactions** — Create and manage contracts with key dates, parties, financial details, and automated task workflows.\n\n**Workflows** — Build reusable checklists (e.g., "Buyer Side - Single Family") that auto-generate tasks when applied to a transaction.\n\n**Contacts** — Manage your people database.\n\n**Calendar** — Visual timeline of all deadlines and key dates across transactions.\n\n**Reports** — Analytics on your pipeline, task completion, financial volume, and more.`,
      },
      {
        id: "gs-2",
        title: "Creating Your First Transaction",
        content: `1. Navigate to **Transactions** from the sidebar.\n2. Click **New Transaction**.\n3. Fill in the property address, purchase price, and key dates (contract date, closing date, inspection deadline, etc.).\n4. Add buyer/seller names and agent information in the People section.\n5. Click **Create Transaction**.\n\n**Pro Tip:** You can also upload a contract PDF and our AI will auto-extract key details like addresses, dates, and prices using OCR.`,
      },
      {
        id: "gs-3",
        title: "Understanding Roles: Admin vs Coordinator",
        content: `**Admin (Super Admin)**\n- Full access to all transactions, regardless of assignment\n- Can create/edit/delete workflow templates\n- Can manage users (add coordinators, impersonate)\n- Can configure company settings and branding\n\n**Coordinator**\n- Sees only transactions assigned to them\n- Can create transactions and add manual tasks\n- Can suggest workflow changes (sent to admin for approval)\n- Can configure their own email/SMTP settings`,
      },
    ],
  },
  {
    category: "Transactions",
    icon: FileText,
    color: "text-blue-600",
    articles: [
      {
        id: "tx-1",
        title: "Managing Key Dates",
        content: `Every transaction has critical deadlines:\n\n- **Contract Date** — When the purchase agreement was executed\n- **Earnest Money Deadline** — Due date for earnest money deposit\n- **Inspection Deadline** — Last day for buyer inspections\n- **Financing Deadline** — When financing must be secured\n- **Appraisal Deadline** — Deadline for appraisal completion\n- **Closing Date** — The big day!\n\n**Dynamic Deadline Tracking:** When you change the closing date, all workflow tasks that reference it will automatically recalculate their due dates. You'll see a confirmation showing which tasks shifted.`,
      },
      {
        id: "tx-2",
        title: "Working with Tasks",
        content: `Tasks can be added two ways:\n\n**1. From Workflows** — Apply a workflow template to auto-generate all tasks with calculated due dates.\n\n**2. Manually** — Click "Add Task" in the transaction detail to create individual tasks.\n\nEach task has:\n- **Type** — Standard Task or Email Task\n- **Due Date** — Set manually or calculated from a workflow formula\n- **Responsible Party** — Assign to a team member or external party\n- **Status** — Open tasks appear at the top; completed tasks collapse into a "Completed" folder\n\n**Completing Tasks:** Click the circle icon next to any task to mark it complete. It will move to the Completed folder. Click the green checkmark to un-complete it.`,
      },
      {
        id: "tx-3",
        title: "Transaction Statuses",
        content: `Transactions move through these stages:\n\n- **Before Inspections** — Initial stage after contract execution\n- **Through Inspections** — Inspection period has passed\n- **Closed** — Transaction successfully closed\n- **Cancelled** — Transaction fell through\n\nClick the Edit button on any transaction to change its status.`,
      },
      {
        id: "tx-4",
        title: "Documents & File Storage",
        content: `Each transaction has a Documents section where you can:\n\n- Upload contracts, inspection reports, disclosures, and other files\n- Organize documents by category (Contracts, Inspections, Disclosures, Other)\n- Download or delete uploaded documents\n\nFiles are stored securely and accessible only to authorized team members.`,
      },
      {
        id: "tx-5",
        title: "Zillow Listing & Get Link",
        content: `Each transaction has a **Zillow Listing** card in the sidebar. You can:\n\n- **Get Link** — Click to auto-search Zillow for the property address. The system will attempt to find the exact listing and populate the URL field.\n- **Add URL** — Manually paste a Zillow URL.\n- **Fetch Listing Data** — After saving a Zillow URL, click this to pull in listing photos, beds/baths, square footage, lot size, year built, and home type.\n\n**Note:** "Get Link" works best with full street addresses. If the auto-search doesn't find the exact listing, it will generate a Zillow search URL you can use to find it manually.`,
      },
      {
        id: "tx-6",
        title: "Notify Parties — Editable Email",
        content: `When key dates change, you can manually notify all parties:\n\n1. Open a transaction and click **Notify Parties** at the top.\n2. The system generates an email with a formatted table of all current key dates.\n3. **Edit** — Click the Edit button to switch to a plain-text editor where you can modify the email body before sending.\n4. **Preview** — Click Preview to see the formatted HTML version of your edits.\n5. **Recipients** — Pre-filled with all transaction parties (buyer, seller, agents, lender, title). Remove any you don't want to include.\n6. Click **Send Notification** to dispatch the email.`,
      },
      {
        id: "tx-7",
        title: "Activity Log & Timestamps",
        content: `Every transaction has an **Activity Log** at the bottom tracking all changes:\n\n- Task completions and status changes\n- Deadline modifications and notifications sent\n- Document uploads and workflow applications\n- Contact updates and notes\n\nEach entry shows the **user name**, a **clock icon**, and the **full date and time** (e.g., "Jun 12, 2026 at 3:45 PM") so you always know exactly when an action occurred.`,
      },
      {
        id: "tx-8",
        title: "Export & Import Transactions (CSV)",
        content: `**Super Admins** can bulk export and import transaction data:\n\n**Export:**\n1. Go to the **Transactions** page.\n2. Click the **Export** button (to the left of New Transaction).\n3. A CSV file will download with all 45+ fields for every transaction in your organization.\n\n**Import:**\n1. Click the **Import** button and select a CSV file.\n2. Rows **with** a transaction_id will update existing transactions.\n3. Rows **without** a transaction_id will create new transactions.\n4. A summary toast will show how many were created, updated, and any errors.\n\n**Tip:** Export first to get the correct column format, then modify the CSV and re-import.`,
      },
    ],
  },
  {
    category: "Workflows",
    icon: GitBranch,
    color: "text-purple-600",
    articles: [
      {
        id: "wf-1",
        title: "Creating a Workflow Template",
        content: `Workflows are reusable task checklists. To create one:\n\n1. Go to **Workflows** and click **New Workflow**.\n2. Name it (e.g., "Buyer Side - Single Family") and set the property type and transaction side.\n3. Click **Add Group** to create task groups (e.g., "Contract Kickoff", "Pre-Closing").\n4. Click **Add Task** within each group to create individual tasks.\n\nWorkflows can be reordered using the up/down arrows on each workflow card.`,
      },
      {
        id: "wf-2",
        title: "Configuring Task Due Dates",
        content: `Each workflow task has a **Due Date Formula**:\n\n- **Days** — Number of days offset\n- **Direction** — Before or After the reference date\n- **Reference Date** — Closing Date, Contract Date, Inspection Deadline, etc.\n- **Business Days** — Check this to skip weekends in the calculation\n- **If Weekend** — Choose to shift to Next Business Day, Previous Business Day, or Don't Shift\n\n**Example:** "5 business days before Closing Date, shifted to next business day if on weekend" means the task will be due 5 weekdays before closing, and if that falls on a Saturday/Sunday, it moves to the following Monday.`,
      },
      {
        id: "wf-3",
        title: "Email Tasks & Templates",
        content: `Set a task's type to **Email** to include an email template:\n\n- **Send To** — Check which transaction parties should receive the email\n- **CC / BCC** — Add additional email addresses (comma-separated)\n- **Subject** — Email subject line with dynamic variables\n- **Body** — Email content with dynamic variables\n\n**Dynamic Variables:** Click "Insert Variable" to add placeholders that auto-fill with transaction data:\n- [Buyer], [Buyer First Name], [Seller], [Seller First Name]\n- [Buyer Agent], [Buyer Agent First Name], [Seller Agent], [Seller Agent First Name]\n- [Lender], [Lender First Name], [Title Company]\n- [Loan Processor], [Loan Processor First Name]\n- [Property Address], [Closing Date], [Contract Date]\n- [Purchase Price], [Earnest Money]`,
      },
      {
        id: "wf-4",
        title: "Task Dependencies & Ordering",
        content: `**Dependencies:** When creating a task, you can set it as "Dependent On" another task. In the workflow display, dependent tasks are visually indented with a dashed border showing the relationship.\n\n**Reordering:** Tasks within a group can be reordered using the up/down arrow buttons in the task editor. The order you set is the order tasks appear when the workflow is applied to a transaction.\n\n**Task Groups** can also be reordered by dragging or using the controls in the editor.`,
      },
      {
        id: "wf-5",
        title: "Applying Workflows to Transactions",
        content: `To apply a workflow to a transaction:\n\n1. Open the transaction detail page.\n2. Scroll to the **Workflow** panel (below Documents).\n3. Select a workflow from the dropdown and click **Apply**.\n\nAll tasks from the workflow will be generated with calculated due dates based on the transaction's key dates. You can change or remove the workflow at any time.`,
      },
    ],
  },
  {
    category: "Contacts & CRM",
    icon: Users,
    color: "text-orange-600",
    articles: [
      {
        id: "ct-1",
        title: "Managing Contacts",
        content: `The Contacts page stores your people database. Each contact has:\n- Name, Email, Phone\n- Role (Buyer, Seller, Lender, Title Company, Agent, etc.)\n- Company name\n\nContacts are used to auto-fill transaction party information.`,
      },
    ],
  },
  {
    category: "Calendar",
    icon: Calendar,
    color: "text-teal-600",
    articles: [
      {
        id: "cal-1",
        title: "Using the Calendar View",
        content: `The Calendar shows all task due dates and key dates across your transactions:\n\n- **Color-coded** events by type (tasks, deadlines, closings)\n- **Month/Week/Day** views for different perspectives\n- **Today button** — Quick jump back to today's date\n- **Click any event** to navigate directly to its transaction\n\n**Export Options:**\n- **Google Calendar** — Subscribe to your deadlines via Google Calendar link\n- **ICS Export** — Download an .ics file for any calendar app`,
      },
    ],
  },
  {
    category: "Reports & Analytics",
    icon: BarChart3,
    color: "text-indigo-600",
    articles: [
      {
        id: "rp-1",
        title: "Understanding the Reports Dashboard",
        content: `The Reports page has 7 analytics sections:\n\n1. **Pipeline Overview** — Transaction counts by status with monthly trend\n2. **Task Completion** — Completion rates, overdue tasks, on-time percentage\n3. **Financial Summary** — Total volume, average price, earnest money totals\n4. **Coordinator Performance** — Tasks and transactions per team member\n5. **Timeline Analysis** — Average days to close, inspection-to-close trends\n6. **Document Tracking** — Upload counts by category\n7. **Communication Stats** — Email reminders and notification activity`,
      },
      {
        id: "rp-2",
        title: "Filtering Reports by Date Range",
        content: `Use the date range filter at the top of the Reports page:\n\n- **From / To** date pickers for custom ranges\n- **Preset buttons:** 30 days, 90 days, 6 months, 1 year\n- **Clear** button to reset to all-time data\n\nThe filter applies to all 7 report sections simultaneously, filtering transactions by their contract date.`,
      },
      {
        id: "rp-3",
        title: "Coordinator Earnings & Agent Filter",
        content: `The **Coordinator Earnings** section tracks closed deals and pending earnings:\n\n- **Summary Cards** — Closed files count, total earned, active files, pending volume, and pending earnings\n- **Fee per Transaction** — Set the coordinator's fee amount and save it\n- **Date Range** — Filter by YTD, Q1, 6 months, or custom dates\n\n**Agent Filter:**\nBoth the Closed Files and Active/Pending Files tables have an **"All Agents"** dropdown in the header. Select a specific agent to see only the transactions where they were the buyer or seller agent. This helps coordinators identify their **top-producing agents**.\n\n**Email My Summary** — Click to receive a formatted earnings report email.`,
      },
    ],
  },
  {
    category: "Email & Notifications",
    icon: Mail,
    color: "text-rose-600",
    articles: [
      {
        id: "em-1",
        title: "Setting Up Email (SMTP)",
        content: `To send email reminders from the app:\n\n1. Go to **Settings** > **SMTP Configuration**.\n2. Enter your Gmail address and an **App Password** (not your regular password).\n3. Set your **Sender Name** — the "From" name recipients see.\n4. Add an **Email Signature** that appears at the bottom of outbound emails.\n\n**Getting a Gmail App Password:**\n1. Go to myaccount.google.com > Security\n2. Enable 2-Factor Authentication if not already on\n3. Search "App Passwords" and generate one for "Mail"`,
      },
      {
        id: "em-2",
        title: "Daily Digest Emails",
        content: `Every morning at 8:00 AM Eastern, the system sends a digest email to team members with configured SMTP settings. The digest includes:\n\n- Tasks due today\n- Overdue tasks\n- Upcoming deadlines (next 3 days)\n- Quick link to log in\n\nThis runs automatically — no setup needed beyond SMTP configuration.`,
      },
    ],
  },
  {
    category: "Admin Features",
    icon: Shield,
    color: "text-amber-600",
    articles: [
      {
        id: "ad-1",
        title: "User Management",
        content: `Admins can manage team members from the **Users** page:\n\n- **Add users** by email — they'll be able to log in via Google\n- **Set roles** — Admin or Coordinator\n- **Remove access** — Deactivate users who leave the team`,
      },
      {
        id: "ad-2",
        title: "Impersonation",
        content: `Super Admins can "impersonate" any coordinator to see the app from their perspective:\n\n1. Go to **Users** page.\n2. Click the impersonate icon next to a coordinator.\n3. You'll see the app as they see it — their transactions, their tasks.\n4. All actions are logged with "via [Your Name]" in the activity log.\n5. Click "Stop Impersonating" in the banner to return to your view.\n\nThis is useful for troubleshooting or assisting coordinators without sharing credentials.`,
      },
      {
        id: "ad-3",
        title: "Company Branding",
        content: `Customize the app's appearance from **Settings**:\n\n- **Company Logo** — Upload your logo to replace the default in the sidebar\n- **Theme** — Choose from 4 built-in themes (The Estate, Midnight, Ocean, Sage)\n\nTheme changes apply app-wide and persist per user.`,
      },
      {
        id: "ad-4",
        title: "Platform Admin — Tenant Management",
        content: `Super Admins have access to the **Platform Admin** page with a Tenants section:\n\n- **Last Active** — Shows when each organization was last used (e.g., "2h ago", "Apr 9, 2026").\n- **Login As** — Click the ⋯ menu on any tenant and select "Login As" to impersonate their owner account. This creates an impersonation session so you can troubleshoot or fix issues directly in their workspace. All actions are logged.\n- **Delete Tenant** — Permanently removes the organization and ALL associated data (users, transactions, workflows, contacts, documents, subscriptions). A confirmation dialog lists everything that will be deleted. This action cannot be undone.\n- **Deactivate** — Temporarily disables the organization without deleting data.\n- **Change Plan** — Override the tenant's subscription plan.`,
      },
    ],
  },
];

/* ── Single Article Accordion ── */
const ArticleItem = ({ article, isOpen, onToggle }) => (
  <Collapsible open={isOpen} onOpenChange={onToggle}>
    <CollapsibleTrigger className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors rounded-lg text-left" data-testid={`article-${article.id}`}>
      {isOpen ? <ChevronDown className="w-4 h-4 text-[var(--t-text-subtle)] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[var(--t-text-subtle)] shrink-0" />}
      <span className="text-sm font-medium text-[var(--t-text)]">{article.title}</span>
    </CollapsibleTrigger>
    <CollapsibleContent>
      <div className="px-11 pb-4 text-sm text-[var(--t-text-muted)] leading-relaxed whitespace-pre-line">
        {article.content.split(/(\*\*[^*]+\*\*)/).map((part, i) => {
          if (part.startsWith("**") && part.endsWith("**")) {
            return <strong key={`${article.title}-b-${i}`} className="text-[var(--t-text)] font-semibold">{part.slice(2, -2)}</strong>;
          }
          return <span key={`${article.title}-s-${i}`}>{part}</span>;
        })}
      </div>
    </CollapsibleContent>
  </Collapsible>
);

/* ── Help Center Page ── */
const HelpCenter = () => {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const [openArticles, setOpenArticles] = useState({});

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return HELP_ARTICLES;
    const q = search.toLowerCase();
    return HELP_ARTICLES.map((cat) => ({
      ...cat,
      articles: cat.articles.filter(
        (a) => a.title.toLowerCase().includes(q) || a.content.toLowerCase().includes(q)
      ),
    })).filter((cat) => cat.articles.length > 0);
  }, [search]);

  const displayCategories = activeCategory
    ? filteredCategories.filter((c) => c.category === activeCategory)
    : filteredCategories;

  const totalArticles = HELP_ARTICLES.reduce((s, c) => s + c.articles.length, 0);

  const toggleArticle = (id) => {
    setOpenArticles((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const startTour = () => {
    localStorage.removeItem("onboarding_complete");
    window.location.href = "/dashboard?tour=1";
  };

  return (
    <div className="p-6 md:p-8 animate-fade-in">
      {/* Header */}
      <div className="page-header -mx-6 md:-mx-8 -mt-6 md:-mt-8 mb-6 px-6 md:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--t-text)] [font-family:var(--t-heading-font)] tracking-tight">
              Help Center
            </h1>
            <p className="text-[var(--t-text-muted)] mt-1">
              {totalArticles} articles across {HELP_ARTICLES.length} categories
            </p>
          </div>
          <Button onClick={startTour} variant="outline" data-testid="restart-tour-btn">
            <Play className="w-4 h-4 mr-2" />
            Restart Guided Tour
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--t-text-subtle)]" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search help articles..."
          className="pl-10 h-11 text-sm"
          data-testid="help-search"
        />
      </div>

      {/* Category Chips */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button
          variant={activeCategory === null ? "default" : "outline"}
          size="sm"
          className={activeCategory === null ? "bg-[var(--t-primary)] hover:bg-[var(--t-primary-hover)]" : ""}
          onClick={() => setActiveCategory(null)}
          data-testid="category-all"
        >
          All
        </Button>
        {HELP_ARTICLES.map((cat) => {
          const Icon = cat.icon;
          return (
            <Button
              key={cat.category}
              variant={activeCategory === cat.category ? "default" : "outline"}
              size="sm"
              className={activeCategory === cat.category ? "bg-[var(--t-primary)] hover:bg-[var(--t-primary-hover)]" : ""}
              onClick={() => setActiveCategory(activeCategory === cat.category ? null : cat.category)}
              data-testid={`category-${cat.category.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon className={`w-3.5 h-3.5 mr-1.5 ${activeCategory === cat.category ? "" : cat.color}`} />
              {cat.category}
              <Badge variant="secondary" className="ml-1.5 text-[10px] h-4 px-1.5">{cat.articles.length}</Badge>
            </Button>
          );
        })}
      </div>

      {/* Articles */}
      {displayCategories.length > 0 ? (
        <div className="space-y-6">
          {displayCategories.map((cat) => {
            const Icon = cat.icon;
            return (
              <Card key={cat.category} className="card-surface" data-testid={`help-section-${cat.category.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center gap-3 px-5 py-4 border-b border-[var(--t-border)]">
                  <div className={`p-2 rounded-md bg-slate-100 ${cat.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h2 className="text-base font-bold text-[var(--t-text)] [font-family:var(--t-heading-font)]">{cat.category}</h2>
                  <Badge variant="outline" className="text-xs ml-auto">{cat.articles.length} articles</Badge>
                </div>
                <CardContent className="p-2">
                  {cat.articles.map((article) => (
                    <ArticleItem
                      key={article.id}
                      article={article}
                      isOpen={openArticles[article.id] || false}
                      onToggle={() => toggleArticle(article.id)}
                    />
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="card-surface">
          <CardContent className="py-12">
            <div className="empty-state">
              <HelpCircle className="empty-state-icon" />
              <p className="empty-state-title">No articles found</p>
              <p className="empty-state-description">Try a different search term.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Tips */}
      <Card className="card-surface mt-6">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[var(--t-border)]">
          <Lightbulb className="w-5 h-5 text-amber-500" />
          <h2 className="text-base font-bold text-[var(--t-text)] [font-family:var(--t-heading-font)]">Quick Tips</h2>
        </div>
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: Clock, tip: "Tasks overdue appear with a red icon. Address them promptly to stay on track." },
              { icon: CheckCircle2, tip: "Completed tasks collapse into a folder so you can focus on what's left." },
              { icon: Mail, tip: "Use [Buyer], [Seller], [Property Address] variables in email templates for auto-personalization." },
            ].map((item, i) => {
              const TipIcon = item.icon;
              return (
                <div key={item.tip} className="flex gap-3 p-3 rounded-lg bg-amber-50/50 border border-amber-100">
                  <TipIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-[var(--t-text-muted)] leading-relaxed">{item.tip}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpCenter;
